import _ from "lodash";
import { useMemo } from "react";
import { DOTS } from "../constants/general";

export default function usePagination({
  totalCount,
  pageSize,
  siblingCount,
  currentPage,
}) {
  if (_.isUndefined(siblingCount)) siblingCount = 1;
  if (!_.isNumber(currentPage)) throw new Error(`currentPage must be a number`);

  return useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);

    const totalPageNumbers = siblingCount + 5;

    // case 1
    // number of pages is less than number of page links to show
    // return [1..totalPageCount]
    if (totalPageNumbers >= totalPageCount) {
      return _.range(1, totalPageCount + 1);
    }

    // Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    // We do not show dots just when there is just one page number to be inserted
    // between the extremes of sibling and the page limits i.e 1 and totalPageCount.
    // Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    // Case 2: No left dots to show, but rights dots to be shown
    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = _.range(1, leftItemCount + 1);

      return [...leftRange, DOTS, totalPageCount];
    }

    // Case 3: No right dots to show, but left dots to be shown
    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = _.range(
        totalPageCount - rightItemCount + 1,
        totalPageCount + 1
      );

      return [firstPageIndex, DOTS, ...rightRange];
    }

    // Case 4: Both left and right dots to be shown
    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = _.range(leftSiblingIndex, rightSiblingIndex + 1);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

    console.error("NOTHING RETURNED FROM usePagination");
    console.log("shouldShowLeftDots", shouldShowLeftDots);
    console.log("shouldShowRightDots", shouldShowRightDots);
  }, [totalCount, pageSize, siblingCount, currentPage]);
}
